const toCard = (student, card) => ({
  id: card.id,
  name: [student.firstName, student.lastName].filter(Boolean).join(' '),
  serviceBranch: card.serviceBranch || null,
  sectionName: card.sectionName || null,
  graduationYear: card.graduationYear || null,
  issuedAt: card.issuedAt || null,
})

/**
 * Flattens the golfer's own cards into one list, newest graduation year first.
 * Only the golfer's SELF student counts, so cards of other students they
 * represent never appear. Students without a card are skipped.
 */
export const flattenGraduateCards = data => {
  const representatives = (data?.currentUser?.representatives ?? []).filter(
    representative => representative.relationshipType === 'SELF',
  )
  const cards = representatives.flatMap(({ student }) =>
    (student?.pgaHopeGraduateCards ?? []).map(card => toCard(student, card)),
  )
  return cards.sort((a, b) => (b.graduationYear ?? 0) - (a.graduationYear ?? 0))
}

// The backend has no verification timestamp, so the card shows when this
// client last fetched it. Older than this, the line reads "Last verified".
export const VERIFIED_FRESH_MS = 5 * 60 * 1000

const yearOf = card => card.graduationYear ?? -Infinity

const issuedAtOf = card => {
  const time = card.issuedAt ? Date.parse(card.issuedAt) : NaN
  return Number.isNaN(time) ? -Infinity : time
}

const isNewer = (card, other) =>
  yearOf(card) !== yearOf(other)
    ? yearOf(card) > yearOf(other)
    : issuedAtOf(card) > issuedAtOf(other)

/**
 * The one card to show when a golfer has several: highest graduation year,
 * then latest issue time, then the first in the list. Null when there are none.
 */
export const getLatestCard = cards =>
  cards.reduce(
    (latest, card) => (latest && !isNewer(card, latest) ? latest : card),
    null,
  )

// Intl can emit a narrow no-break space before AM/PM; normalize it so the
// text is identical across browsers and Node versions.
export const formatVerifiedDate = date => {
  const day = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const time = date
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    .replace(/\u202f/g, ' ')
  return `${day} · ${time}`
}

/**
 * 'fresh' when the last fetch succeeded recently, 'stale' when it is old or
 * the latest fetch failed, and null when there is nothing to say.
 */
export const getVerificationState = ({ verifiedOn, failed, now }) => {
  if (!verifiedOn) return null
  if (failed || now - verifiedOn.getTime() > VERIFIED_FRESH_MS) return 'stale'
  return 'fresh'
}

const LONG_NAME_LENGTH = 28
const LONG_WORD_LENGTH = 14

// Long names shrink instead of truncating, so the full name stays readable.
export const isLongName = name =>
  name.length > LONG_NAME_LENGTH ||
  name.split(/\s+/).some(word => word.length > LONG_WORD_LENGTH)

export const getCardFilename = ({ name, graduationYear }) => {
  const slug = (name || 'graduate')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return ['pga-hope-card', slug, graduationYear].filter(Boolean).join('-')
}
